import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { provideHooks } from 'redial';
import styled, { css, ThemeProvider } from 'styled-components';
import Button from 'components/MuiComponents/Button';
import { withRouter } from 'react-router';
import { Link as LinkElement, Element } from 'react-scroll';
import * as placesActions from 'redux/modules/places';
import * as plannerActions from 'redux/modules/planner';
import customizeBookingReducer, * as customizeBookingActions from 'redux/modules/customizeBooking';
import PageSectionHeaders from 'components/PageSectionHeaders/Loadable';
import classnames from 'classnames/bind';
import { withTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import isEmpty from 'lodash.isempty';

import { VERTICAL_ORIENTATION, HORIZONTAL_ORIENTATION } from 'react-dates/lib/constants';
import DateRangePickerWrapper from 'components/DateRangePicker/DateRangePickerLoadable';
import escapeDoubleQuote from 'utils/escapeDoubleQuote';
import { sendGtagEvent } from 'utils/gtag';

import CountrySelect from './CountrySelect';
import theme from '../../theme/styled-theme';

import config from '../../config';

const Header = styled.h1`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const CtaLink = styled.div`
  cursor: pointer;
  color: ${props => props.theme.primaryGreen};
  font-size: 1.25rem;
`;

const CountryAndDateFrom = styled.div`
  display: flex;
  margin-bottom: 1rem;
`;

const DatePicker = styled.div`
  .DateInput .DateInput_input {
    height: 48px;
  }

  .DateRangePickerInput__withBorder {
    overflow: hidden;
    border-radius: 4px;
  }
`;

@provideHooks({
  inject: ({ store }) => store.inject({
    customizeBooking: customizeBookingReducer,
  }),
})
@connect(state => ({
  mobileDetect: state.mobileDetect,
  plan: state.planner?.plan,
  selectedCountry: state?.customizeBooking?.selectedCountry,
}), {
  ...placesActions,
  ...customizeBookingActions,
  ...plannerActions,
})
@withRouter
@withTranslation(['common', 'meta'])
class Customize extends Component {
  static propTypes = {
    history: PropTypes.objectOf(PropTypes.any).isRequired,
    t: PropTypes.func.isRequired,
    changeTripDate: PropTypes.func.isRequired,
    addDaysToPlannerState: PropTypes.func.isRequired,
    plan: PropTypes.objectOf(PropTypes.any).isRequired,
    mobileDetect: PropTypes.objectOf(PropTypes.any),
    changeCustomizeCountry: PropTypes.func.isRequired,
    selectedCountry: PropTypes.string.isRequired,
  };

  static defaultProps = {
    mobileDetect: {},
  };

  constructor(props) {
    super(props);

    this.renderForm = this.renderForm.bind(this);
    this.onTripDateChange = this.onTripDateChange.bind(this);
    this.changeReduxTripDate = this.changeReduxTripDate.bind(this);
    this.renderYellowCta = this.renderYellowCta.bind(this);

    this.state = {
      startDate: null,
      endDate: null,
    };
  }

  onTripDateChange({ startDate, endDate }) {
    this.setState({ startDate, endDate });
  }

  changeReduxTripDate() {
    const {
      changeTripDate,
      addDaysToPlannerState,
      plan,
    } = this.props;
    const { startDate, endDate } = this.state;

    let days = 0;

    if (startDate && endDate) {
      const duration = moment.duration(endDate.diff(startDate));
      days = Math.round(duration.asDays()) + 1; // duration can sometimes be floating number

      if (days !== 0 && plan.length < days) {
        const daysToAdd = days - plan.length;
        addDaysToPlannerState(daysToAdd);
      }
    }

    changeTripDate({
      startDate: startDate && startDate.format('YYYY-MM-DD'),
      endDate: endDate && endDate.format('YYYY-MM-DD'),
      numberOfDays: days,
    });
  }

  renderPageStructuredData() {
    const { t } = this.props;
    const name = escapeDoubleQuote(t('meta:META.CUSTOMIZE.PAGE_STRUCTURE_NAME'));
    const description = escapeDoubleQuote(t('meta:META.CUSTOMIZE.PAGE_STRUCTURE_DESCRIPTION'));

    return (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "http://schema.org/",
            "@type": "Service",
            "name": "${name}",
            "image": "https://assets.ownrides.com/misc/Email%20Campaign/6d899520-22ec-4ba9-b57f-5f299701a72b.jpg?response-content-disposition=inline&X-Amz-Security-Token=FQoDYXdzEEQaDB9RFH2Y%2FckmfU3dlCL6AUgArGyQixBG7fdGeExWUkqRLezK2%2BsGqBA1coVAdER8x927%2B%2BmWEvJmK%2FsucuFR8MyR4wc%2FTA%2B5FonU4zUbFoa45647yiXY4C%2FEAWRW8pgnu9D76x47rMHQPcf4um5X1jErUXmxj8%2FcZ0FEJhr%2BvmEeIoAHzf1IRGrQneLfH10qeosa9wsu1Y9MiA87d6%2FcRYwv7z3jc9q6R6yG9xpT0JbPuVD68g09yz2ZOlL7HXpYW852%2B0wB%2FZKZ1qBq6g2RFcXVCYV3FI6BNwF7wBsnfJ3q38Fu%2F4AXe6bxgKRpMlDinKnZgWoUbEKSfpan6hJ2LTfvWpCELsfvN90o%2FuXXxwU%3D&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20170418T110855Z&X-Amz-SignedHeaders=host&X-Amz-Expires=300&X-Amz-Credential=ASIAJ2IV3AQUOKXFTWBQ%2F20170418%2Fap-northeast-1%2Fs3%2Faws4_request&X-Amz-Signature=e357d91d3176dcc1a60bed8c21f7427a9baf5f15d48034ee1fa61ab88f4f4845",
            "description": "${description}"
          }`
        }}
      />
    );
  }

  renderBreadcrumbStructuredData = () => (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `{
          "@context": "http://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "item": {
              "@id": "https://www.ownrides.com",
              "name": "OWNRIDES"
            }
          },{
            "@type": "ListItem",
            "position": 2,
            "item": {
              "@id": "https://www.ownrides.com/tours",
              "name": "Itinerary"
            }
          },{
            "@type": "ListItem",
            "position": 3,
            "item": {
              "@id": "https://www.ownrides.com/itinerary/customize",
              "name": "Custom Trip"
            }
          }]
        }`
      }}
    />
  );

  renderYellowCta(styles, label = 'Plan Now', buttonColor = '', colorType = 'primary') {
    const { startDate, endDate } = this.state;
    return (
      <Button
        variant="contained"
        color={colorType}
        overwritestyles={css`
          padding: 14px 28px;
          ${buttonColor ? `background: ${buttonColor}` : ''};
        `}
        disabled={isEmpty(startDate) || isEmpty(endDate)}
      >
        { label }
      </Button>
    );
  }

  renderMegaHeader(styles) {
    const { t, history, selectedCountry } = this.props;
    const { startDate, endDate } = this.state;

    return (
      <div className={styles.megaHeader}>
        <div className={`${styles.megaContainer} container`}>
          <div className={styles.content}>
            <Header className={`${styles.header} regular`}>
              <span>Customize Your Trip</span>
            </Header>
            <h2 className={styles.subheader}>{t('CUSTOMIZE_LANDING.BANNER_SUBTITLE')}</h2>
            <Element name="plan-now" />

            { this.renderForm() }

            <Button
              variant="contained"
              color="secondary"
              overwritestyles={css`
                padding: 14px 28px;
                background-color: #ffffff;
              `}
              disabled={isEmpty(startDate) || isEmpty(endDate)}
              onClick={e => {
                if (isEmpty(startDate) || isEmpty(endDate)) return;
                e.preventDefault();
                this.changeReduxTripDate();
                sendGtagEvent('Clicked Plan Now on MegaHeader', 'Customize Landing Page', 'MegaHeader');
                return history.push(`/itinerary/customize/planner?country=${selectedCountry}`);
              }}
            >
              { t('CUSTOMIZE_LANDING.BANNER_CTA') }
            </Button>
          </div>
          <div className={styles.videoContainer}>
            <div className={styles.video}>
              <div className={styles.videoBackground}>
                <div className={styles.videoForeground}>
                  <iframe
                    title="customize overview"
                    src="https://www.youtube.com/embed/HzZv-aWMyd4?controls=0&showinfo=0&rel=0&autoplay=1&loop=1&playlist=HzZv-aWMyd4"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderTestimonial(styles) {
    const { t } = this.props;
    return (
      <section className={styles.testimonialContainer}>
        <div className={styles.testimonial}>
          <div className={styles.bubbleContainer}>
            <div className={styles.bubble}>{t('CUSTOMIZE_LANDING.HIGHTLIGHT_TESTIMONIAL')}</div>
          </div>
          <span className={styles.userName}>Bryant Villanueva, Philippines</span>
        </div>
      </section>
    );
  }

  renderFeatures(styles, images) {
    const { t } = this.props;
    const {
      quotation: { oneX: quotation1x, twoX: quotation2x, three: quotation3x },
      multiday: { oneX: multiday1x, twoX: multiday2x, three: multiday3x },
      comprehansive: { oneX: comprehansive1x, twoX: comprehansive2x, three: comprehansive3x }
    } = images;

    return (
      <section className={styles.featureSectionContainer}>
        <div className="container">
          <h2 className={`${styles.sectionTitle} regular`}>{t('CUSTOMIZE_LANDING.FEATURE_SECTION_TITLE')}</h2>
          <h3 className={styles.sectionSubtitle}>{t('CUSTOMIZE_LANDING.FEATURE_SECTION_SUBTITLE')}</h3>
          <div className={styles.featureContainer}>
            <div className={styles.content}>
              <h2 className={styles.header}>{t('CUSTOMIZE_LANDING.FEATURE_1_TITLE')}</h2>
              <p className={styles.details}>{t('CUSTOMIZE_LANDING.FEATURE_1_SUBTITLE')}</p>
              <CtaLink>
                <LinkElement
                  to="plan-now"
                  duration={500}
                  offset={-200}
                  smooth
                >
                  { `${t('CUSTOMIZE_LANDING.FEATURE_CTA')} →` }
                </LinkElement>
              </CtaLink>
            </div>
            <div className={styles.image}>
              <picture>
                <source srcSet={`${quotation3x} 3x`} media="(min-width: 1200px)" />
                <img srcSet={`${quotation1x}, ${quotation2x} 2x`} alt="Quotation on the fly" />
              </picture>
            </div>
          </div>

          <div className={`${styles.featureContainer} ${styles.reverse}`}>
            <div className={styles.content}>
              <h2 className={styles.header}>{t('CUSTOMIZE_LANDING.FEATURE_2_TITLE')}</h2>
              <p className={styles.details}>{t('CUSTOMIZE_LANDING.FEATURE_2_SUBTITLE')}</p>
              <CtaLink>
                <LinkElement
                  to="plan-now"
                  duration={500}
                  offset={-200}
                  smooth
                >
                  { `${t('CUSTOMIZE_LANDING.FEATURE_CTA')} →` }
                </LinkElement>
              </CtaLink>
            </div>
            <div className={styles.image}>
              <picture className={styles.shadow}>
                <source srcSet={`${multiday3x} 3x`} media="(min-width: 1200px)" />
                <img srcSet={`${multiday1x}, ${multiday2x} 2x`} alt="Quotation on the fly" />
              </picture>
            </div>
          </div>

          <div className={styles.featureContainer}>
            <div className={styles.content}>
              <h2 className={styles.header}>{t('CUSTOMIZE_LANDING.FEATURE_3_TITLE')}</h2>
              <p className={styles.details}>{t('CUSTOMIZE_LANDING.FEATURE_3_SUBTITLE')}</p>
              <CtaLink>
                <LinkElement
                  to="plan-now"
                  duration={500}
                  offset={-200}
                  smooth
                >
                  { `${t('CUSTOMIZE_LANDING.FEATURE_CTA')} →` }
                </LinkElement>
              </CtaLink>
            </div>
            <div className={styles.image}>
              <picture className={styles.shadow}>
                <source srcSet={`${comprehansive3x} 3x`} media="(min-width: 1200px)" />
                <img srcSet={`${comprehansive1x}, ${comprehansive2x} 2x`} alt="Quotation on the fly" />
              </picture>
            </div>
          </div>
          <div className={styles.featureSectionCTA}>
            <LinkElement
              to="plan-now"
              duration={500}
              offset={-200}
              smooth
            >
              <Button
                variant="contained"
                color="primary"
                overwritestyles={css`
                  padding: 14px 28px;
                `}
              >
                { t('CUSTOMIZE_LANDING.FEATURE_SECTION_BOTTOM_CTA') }
              </Button>
            </LinkElement>
          </div>
        </div>
      </section>
    );
  }

  renderCreateTripCTABanner(
    styles,
    background,
    {
      multidayPreview, quotationPreview, easyToUsePreview, routePreview
    }
  ) {
    const { t } = this.props;
    return (
      <section
        className={styles.createTripCTAContainer}
        style={{
          background: `url("${background || ''}")  center center / cover`
        }}
      >
        <div className={`${styles.createTrip} container`}>
          <h3 className={styles.createTripCTAHeader}>
            <span>{t('CUSTOMIZE_LANDING.PREVIEW_TITLE')}</span>
          </h3>
          <div className={styles.previewsContainer}>
            <div className={styles.preview}>
              <img src={multidayPreview} alt={t('CUSTOMIZE_LANDING.PREVIEW_1')} />
              <h3 className={styles.description}>{t('CUSTOMIZE_LANDING.PREVIEW_1')}</h3>
            </div>

            <div className={styles.preview}>
              <img src={quotationPreview} alt={t('CUSTOMIZE_LANDING.PREVIEW_2')} />
              <h3 className={styles.description}>{t('CUSTOMIZE_LANDING.PREVIEW_2')}</h3>
            </div>

            <div className={styles.preview}>
              <img src={easyToUsePreview} alt={t('CUSTOMIZE_LANDING.PREVIEW_3')} />
              <h3 className={styles.description}>{t('CUSTOMIZE_LANDING.PREVIEW_3')}</h3>
            </div>

            <div className={styles.preview}>
              <img src={routePreview} alt={t('CUSTOMIZE_LANDING.PREVIEW_4')} />
              <h3 className={styles.description}>{t('CUSTOMIZE_LANDING.PREVIEW_4')}</h3>
            </div>
          </div>
        </div>
      </section>
    );
  }

  renderFreeCancelation(styles, cx) {
    const { t, history, selectedCountry } = this.props;
    const { startDate, endDate } = this.state;

    return (
      <section className={styles.cancelationSection}>
        <div className={styles.contentContainer}>
          <PageSectionHeaders
            title={t('CUSTOMIZE_LANDING.FREE_CANCELLATION_TITLE')}
            subtitle={t('CUSTOMIZE_LANDING.FREE_CANCELLATION_SUBTITLE')}
            center
            alwaysCenter
          />

          { this.renderForm() }
          <div className={styles.centerCTA}>
            <Button
              variant="contained"
              color="primary"
              overwritestyles={css`
                padding: 14px 28px;
              `}
              disabled={isEmpty(startDate) || isEmpty(endDate)}
              onClick={e => {
                if (isEmpty(startDate) || isEmpty(endDate)) return;
                if (e.metaKey || e.ctrlKey) return;
                e.preventDefault();
                this.changeReduxTripDate();
                sendGtagEvent('Clicked Get Started', 'Customize Landing Page', 'Free Cancelation Fee');
                if (__SERVER__ && window.swUpdate) {
                  window.location = `/itinerary/customize/planner?country=${selectedCountry}`;
                  return;
                }
                return history.push(`/itinerary/customize/planner?country=${selectedCountry}`);
              }}
            >
              { t('CUSTOMIZE_LANDING.FREE_CANCELLATION_CTA') }
            </Button>
          </div>
        </div>
        <div className={styles.drawingContainer}>
          <div className={cx('circle', 'circle1')} />
          <div className={cx('circle', 'circle2')} />
          <div className={cx('circle', 'circle3')} />
          <div className={cx('circle', 'circle4')} />
          <div className={cx('circle', 'circle5')} />
          <div className={cx('triangle', 'triangle1')} />
          <div className={cx('triangle', 'triangle2')} />
        </div>
      </section>
    );
  }

  renderForm() {
    const { mobileDetect, changeCustomizeCountry, selectedCountry } = this.props;
    const { startDate, endDate } = this.state;

    return (
      <CountryAndDateFrom>
        <CountrySelect
          country={selectedCountry}
          onChange={changeCustomizeCountry}
        />
        <DatePicker>
          <DateRangePickerWrapper
            onTripDateChange={this.onTripDateChange}
            startDate={startDate}
            endDate={endDate}
            withFullScreenPortal={mobileDetect.isMobile && !mobileDetect.isTablet}
            orientation={mobileDetect.isMobile && !mobileDetect.isTablet ? VERTICAL_ORIENTATION : HORIZONTAL_ORIENTATION}
            small={mobileDetect.isMobile && !mobileDetect.isTablet}
          />
        </DatePicker>
      </CountryAndDateFrom>
    );
  }

  render() {
    const styles = require('./Customize.scss');
    const cdnPrefix = `${config.cdnUrl}/misc/icons`;
    const face = `${cdnPrefix}/customize-landing/BryantVillanueva.jpg`;
    const quotation1x = `${cdnPrefix}/customize-landing/quotation-abstract.png`;
    const quotation2x = `${cdnPrefix}/customize-landing/quotation-abstract@2x.png`;
    const quotation3x = `${cdnPrefix}/customize-landing/quotation-abstract@3x.png`;
    const multiday1x = `${cdnPrefix}/customize-landing/multipleday-abstract.jpg`;
    const multiday2x = `${cdnPrefix}/customize-landing/multipleday-abstract@2x.jpg`;
    const multiday3x = `${cdnPrefix}/customize-landing/multipleday-abstract@3x.jpg`;
    const comprehansive1x = `${cdnPrefix}/customize-landing/comprehansive-abstract.jpg`;
    const comprehansive2x = `${cdnPrefix}/customize-landing/comprehansive-abstract@2x.jpg`;
    const comprehansive3x = `${cdnPrefix}/customize-landing/comprehansive-abstract@3x.jpg`;
    const nickBackground = `${cdnPrefix}/customize-landing/nick-scheerbart-54272.jpg`;
    const multidayPreview = `${cdnPrefix}/customize-landing/multiple-day-preview.jpg`;
    const quotationPreview = `${cdnPrefix}/customize-landing/quotation-preview.jpg`;
    const easyToUsePreview = `${cdnPrefix}/customize-landing/easy-to-customize-preview.jpg`;
    const routePreview = `${cdnPrefix}/customize-landing/route-preview.jpg`;

    const cx = classnames.bind(styles); // eslint-disable-line
    const { t } = this.props;

    return (
      <ThemeProvider theme={theme}>
        <div className={styles.root}>
          <Helmet title={t('meta:META.CUSTOMIZE.TITLE')} />
          <Helmet
            meta={[
              {
                name: 'description',
                content: t('meta:META.CUSTOMIZE.DESCRIPTION')
              },
              {
                property: 'og:title',
                content: t('meta:META.CUSTOMIZE.OG.TITLE')
              },
              {
                property: 'og:description',
                content: t('meta:META.CUSTOMIZE.OG.DESCRIPTION')
              },
              { property: 'og:url', content: `${config.app.url}/itinerary/customize` },
              { id: 'english-canonical-url', content: '' }
            ]}
          />
          {this.renderPageStructuredData()}
          {this.renderBreadcrumbStructuredData()}
          {this.renderMegaHeader(styles)}
          {this.renderTestimonial(styles, face)}
          {this.renderFeatures(styles, {
            quotation: {
              oneX: quotation1x,
              twoX: quotation2x,
              three: quotation3x
            },
            multiday: {
              oneX: multiday1x,
              twoX: multiday2x,
              three: multiday3x
            },
            comprehansive: {
              oneX: comprehansive1x,
              twoX: comprehansive2x,
              three: comprehansive3x
            }
          })}
          {this.renderCreateTripCTABanner(styles, nickBackground, {
            multidayPreview,
            quotationPreview,
            easyToUsePreview,
            routePreview
          })}
          {this.renderFreeCancelation(styles, cx)}
        </div>
      </ThemeProvider>
    );
  }
}

export default Customize;
